import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ClearCacheResult {
  /** Number of entries evicted; -1 means the whole cache was cleared. */
  cleared: number;
  scope: string;
}

/** Cache keys held by one platform service. */
export interface ServiceCacheKeys {
  service: string;
  baseUrl: string;
  /** False when the service could not be reached — distinct from "reachable but empty". */
  reachable: boolean;
  /**
   * False when the service is declared but has no base URL configured, so nothing was attempted.
   * A deployment gap rather than an outage — these used to be dropped server-side, which made the
   * console read as healthy while that service's cache was untouchable.
   */
  configured?: boolean;
  error?: string | null;
  keys: string[];
}

/** Aggregated listing across every platform service. */
export interface PlatformCacheKeys {
  search: string | null;
  totalKeys: number;
  services: ServiceCacheKeys[];
}

/** Outcome of clearing one service's cache. */
export interface ServiceClearResult {
  service: string;
  reachable: boolean;
  /** @see ServiceCacheKeys.configured */
  configured?: boolean;
  error?: string | null;
  cleared: number;
}

export interface PlatformClearResult {
  scope: string;
  totalCleared: number;
  services: ServiceClearResult[];
}

// Admin cache console. Two levels:
//  - findKeys/clear act on the Subscription Management API's own in-memory cache.
//  - listAll/clearAll span every platform service (POS, Inventory, marketplace, storefront) by
//    fanning out server-side, so the browser never handles the shared cache secret.
// Used after editing reference data — countries (24h), categories (up to 12h downstream), settings.
@Injectable({ providedIn: 'root' })
export class CacheAdminService {
  private readonly baseUrl = environment.subscriptionApiUrl + '/api/admin/cache';

  constructor(private http: HttpClient) { }

  findKeys(search: string): Observable<string[]> {
    const params = new HttpParams().set('search', search);
    return this.http.get<string[]>(`${this.baseUrl}/keys`, { params });
  }

  /** Clear entries whose key contains `search`; omit to clear the entire cache. */
  clear(search?: string): Observable<ClearCacheResult> {
    return this.http.post<ClearCacheResult>(`${this.baseUrl}/clear`, { search: search ?? null });
  }

  /** Every cache entry across the platform, grouped by service. Omit `search` to list everything. */
  listAll(search?: string): Observable<PlatformCacheKeys> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<PlatformCacheKeys>(`${this.baseUrl}/all`, { params });
  }

  /** Clear matching entries in every service at once; omit `search` to clear all caches. */
  clearAll(search?: string, reason?: string): Observable<PlatformClearResult> {
    return this.http.post<PlatformClearResult>(`${this.baseUrl}/clear-all`, { search: search ?? null, reason });
  }

  /**
   * Evict ONE entry by its exact key in ONE service — the per-key ✕ in the listing.
   * Never fans out and never substring-matches, so it can't take out longer keys
   * that merely contain the clicked one.
   */
  evictKey(service: string, key: string): Observable<ServiceClearResult> {
    return this.http.post<ServiceClearResult>(`${this.baseUrl}/evict-key`, { service, key });
  }

  /**
   * Force terminals (desktop/browser POS clients) to delete their LOCAL cached data —
   * IndexedDB + legacy web-storage caches. One business, or platform-wide when omitted.
   * Connected terminals purge instantly over SignalR; closed/offline ones purge on next
   * launch via the durable stamp this call records. Server caches are untouched.
   */
  purgeTerminals(businessId?: string, reason?: string): Observable<PurgeTerminalsResult> {
    return this.http.post<PurgeTerminalsResult>(`${this.baseUrl}/purge-terminals`, { businessId: businessId ?? null, reason });
  }
}

export interface PurgeTerminalsResult {
  /** "all businesses" or the business id the purge was scoped to. */
  scope: string;
  /** UTC stamp terminals compare against (ISO). */
  purgedAt: string;
}
